import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';

const BRAND_FAQS = [
  { q: 'How much does this cost?',                          a: "Every campaign is custom-quoted to match the executive, the deliverable, and your reach goals. We work with small first-time tests and large multi-channel campaigns alike. We'll scope a price you're comfortable with on the kick-off call — no retainers, no setup fees, no surprise invoices." },
  { q: 'Can we run a pilot before committing?',             a: 'Absolutely. Most brands start with a single endorsement campaign or a 3-exec survey to see what works. No long-term commitments. You graduate to retainers when (and if) you want to.' },
  { q: 'How long does a campaign take?',                    a: 'A briefed campaign typically matches with verified execs within 48 hours and delivers within 7–14 days, depending on the offering type. Video and consulting projects take longer.' },
  { q: 'What industries do you cover?',                     a: 'SaaS, automotive, fintech, healthtech, manufacturing, retail, and growing. If your buyers are on LinkedIn or read industry newsletters, our execs are already there.' },
  { q: 'Why not just reach out to execs directly?',         a: "You can — but it's slow, awkward, and unscalable. Vetting execs, negotiating rates, chasing deliverables, processing payouts to 12 different people. We handle all of it." },
  { q: 'Do you offer a managed service?',                   a: 'Every Freddy Leads campaign is concierge-managed by default — our team handles briefing, matching, approvals, and reporting end-to-end. You stay in the loop without doing the legwork.' },
  { q: "What if a deliverable isn't what we asked for?",    a: 'Free revisions. You only pay when you approve a final deliverable. If an exec ghosts or delivers off-brief, you pay nothing and we re-match you.' },
  { q: 'How does pricing work for retainers?',              a: 'Monthly bundles lock in your top 1–3 execs for recurring deliverables (e.g. 2 posts/mo + 1 advisory call). Cheaper per deliverable than ad-hoc, with priority scheduling — we scope the exact mix on a kick-off call.' },
];

const EXEC_FAQS = [
  { q: 'How do payouts work?',                              a: "When a brand approves your deliverable, you're paid within 48 hours via Stripe. No invoicing, no chasing, no 60-day net terms." },
  { q: "Can I decline a brand deal I don't like?",          a: "100%. We never auto-assign. Every match is opt-in — you see the brand, the deliverable, the rate, and the brief before you accept." },
  { q: 'Do I need a minimum follower count?',               a: "No. We work with both macro and micro execs. What matters is real industry context, consistent engagement, and a loyal audience — not vanity metrics." },
  { q: 'How much can I make?',                              a: 'Top execs on the platform earn $5K–$50K/month across endorsements, advisory calls, consulting, and training data. Most execs do $1K–$5K/month part-time.' },
  { q: 'Do you take a cut?',                                a: "We charge brands a platform fee on top of your rate — you keep 100% of what you set as your rate. You set your own pricing per offering." },
  { q: 'Can I pause or quit anytime?',                      a: "Yep. Toggle your availability off in the dashboard and you'll see no new matches. Exit anytime. We don't lock execs into exclusivity." },
  { q: 'What kinds of work can I offer?',                   a: '12 offering types: endorsement posts, written testimonials, video testimonials, surveys, feedback sessions, advisory calls, consulting projects, custom packages, retainers, webinars, sales-pitch meetings, and AI training Q&A.' },
];

function AccordionItem({ q, a, idx, prefix }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10" data-testid={`${prefix}-faq-${idx}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-4 py-5 sm:py-6 text-left hover:text-[#FF4343] transition-colors"
        aria-expanded={open}
        data-testid={`${prefix}-faq-toggle-${idx}`}
      >
        <span className="font-display text-base sm:text-lg font-semibold text-white pr-4">{q}</span>
        <span className="flex-shrink-0 w-7 h-7 border border-white/20 flex items-center justify-center">
          {open ? <Minus className="w-3.5 h-3.5 text-[#FF4343]" /> : <Plus className="w-3.5 h-3.5" />}
        </span>
      </button>
      {open && (
        <div className="pb-5 sm:pb-6 pr-12 text-sm text-[#9CA3AF] leading-relaxed animate-fade-in-up" data-testid={`${prefix}-faq-answer-${idx}`}>
          {a}
        </div>
      )}
    </div>
  );
}

export default function FAQSection() {
  const [tab, setTab] = useState('brands');
  const items = tab === 'brands' ? BRAND_FAQS : EXEC_FAQS;
  const prefix = tab === 'brands' ? 'brand' : 'exec';

  return (
    <section className="py-24 sm:py-32 border-t border-white/5" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-10 sm:mb-12">
          <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.3em] text-[#FF4343] mb-3">★ FAQs ★</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Frequently <span className="text-[#FF4343]">asked</span>.
          </h2>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 justify-center mb-8 sm:mb-10" role="tablist">
          <button
            onClick={() => setTab('brands')}
            role="tab"
            aria-selected={tab === 'brands'}
            data-testid="faq-tab-brands"
            className={`px-5 sm:px-8 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold uppercase tracking-wider border ${tab === 'brands' ? 'bg-[#FF4343] text-[#090E33] border-[#FF4343]' : 'border-white/15 text-[#9CA3AF] hover:text-white'}`}
          >
            For Brands
          </button>
          <button
            onClick={() => setTab('execs')}
            role="tab"
            aria-selected={tab === 'execs'}
            data-testid="faq-tab-execs"
            className={`px-5 sm:px-8 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold uppercase tracking-wider border ${tab === 'execs' ? 'bg-[#FF4343] text-[#090E33] border-[#FF4343]' : 'border-white/15 text-[#9CA3AF] hover:text-white'}`}
          >
            For Executives
          </button>
        </div>

        <div className="bg-[#111827] border border-white/10 px-5 sm:px-8" data-testid="faq-list">
          {items.map((f, i) => (
            <AccordionItem key={`${prefix}-${i}`} idx={i} prefix={prefix} q={f.q} a={f.a} />
          ))}
        </div>
      </div>
    </section>
  );
}
